import { Link } from '@tanstack/react-router';

export const PrivacyHeader = ({ date, title }: { date: string; title: string }) => (
  <div className="flex flex-col gap-2">
    <p className="font-mono text-xs md:text-sm uppercase text-gray-400">
      {date}
    </p>
    <h1 className="font-medium text-3xl md:text-4xl text-white">
      {title}
    </h1>
  </div>
);

export const SectionWithText = ({ title, text }: { title: string; text: string }) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <p className="text-base leading-relaxed">{text}</p>
  </div>
);

interface LawfulBasisItem {
  title: string;
  description: string;
}

export const LawfulBasisSection = ({
  title,
  intro,
  items
}: {
  title: string;
  intro: string;
  items: LawfulBasisItem[]
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <div className="text-base">
      <p className="leading-relaxed mb-0">{intro}</p>
      <ul className="list-disc">
        {items.map((item, index) => (
          <li key={index} className="mb-0 ml-6">
            <span className="leading-relaxed">
              <strong>{item.title}:</strong> {item.description}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const DataSharingSection = ({
  title,
  intro,
  items,
  conclusion
}: {
  title: string;
  intro: string;
  items: string[];
  conclusion: string;
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <div className="text-base">
      <p className="leading-relaxed mb-0">{intro}</p>
      <ul className="list-disc mb-0">
        {items.map((item, index) => (
          <li key={index} className="mb-0 ml-6">
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
      <p className="leading-relaxed">{conclusion}</p>
    </div>
  </div>
);

export const BulletListSection = ({ title, items }: { title: string; items: string[] }) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <ul className="list-disc text-base ml-6">
      {items.map((item, index) => (
        <li key={index} className="leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const YourRightsSection = ({
  title,
  intro,
  rights,
  requestsText
}: {
  title: string;
  intro: string;
  rights: string[];
  requestsText: string;
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <div className="text-base">
      <p className="leading-relaxed mb-0">{intro}</p>
      <ul className="list-disc mb-0 ml-6">
        {rights.map((right, index) => (
          <li key={index} className="leading-relaxed">
            {right}
          </li>
        ))}
      </ul>
      <p className="leading-relaxed">
        {requestsText}{' '}
        <a
          className="underline hover:no-underline"
          href="mailto:info@trypillian.com"
        >
          info@trypillian.com
        </a>
        .
      </p>
    </div>
  </div>
);

export const DPOSection = ({
  title,
  contact,
  complaint,
  orByCall
}: {
  title: string;
  contact: string;
  complaint: string;
  orByCall: string;
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <p className="text-base leading-relaxed">
      {contact}{' '}
      <a
        className="underline hover:no-underline"
        href="mailto:info@trypillian.com"
      >
        info@trypillian.com
      </a>
      .<br />
      {complaint}{' '}
      <a
        className="underline hover:no-underline"
        href="https://www.ico.org.uk/"
        target="_blank"
        rel="noopener noreferrer"
      >
        www.ico.org.uk
      </a>
      {' '}{orByCall}{' '}
      <a
        className="underline hover:no-underline"
        href="tel:03031231113"
      >
        0303 123 1113
      </a>
      .
    </p>
  </div>
);

export const ChangesSection = ({
  title,
  text,
  effective
}: {
  title: string;
  text: string;
  effective: string;
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <div className="text-base leading-relaxed space-y-4">
      <p>{text}</p>
      <p>{effective}</p>
    </div>
  </div>
);

export const SectionWithLink = ({
  title,
  beforeLink,
  linkText,
  linkTo,
  afterLink
}: {
  title: string;
  beforeLink: string;
  linkText: string;
  linkTo: string;
  afterLink: string;
}) => (
  <div className="flex flex-col gap-2.5 md:gap-3">
    <h2 className="font-medium text-2xl md:text-3xl">{title}</h2>
    <p className="text-base leading-relaxed">
      {beforeLink}{' '}
      <Link to={linkTo} className="underline hover:no-underline">
        {linkText}
      </Link>
      {afterLink}
    </p>
  </div>
);
